import { FreedataArgsVO as BaseFreedataArgsVO, IVo, entity, field } from '@wizeoo/akm-compute-lib';
import { WizTranslationManager } from '@wizeoo/akm-component-lib';
import { FreeDataArgs, TranslatedEmb } from '@wizeoo/akm-message-lib';
import { Model } from '../../model/model';
import { TranslatableLabelVO } from '../common/translation/translatable-label.vo';

/** 
 * structure d'un champ complémentaire sur un FreeData.
 * Sert de matrice de construction pour les moreArgs des FDVs
 */
@entity('wizeoo.allkeyme.core.message.FreeDataArgs', 'BU')
export class FreedataArgsVO extends BaseFreedataArgsVO implements IVo {

  /** utilisation du partial pour instantiation */
  constructor(model: Model, pSource?: FreeDataArgs) {
    super(model, pSource);
  }

  public get id(): number {
    return this.source.id;
  }

  public set id(id: number) {
    this.source.id = id;
  }
  public get uid(): string {
    return this.source.uid;
  }
  public set uid(uid: string) {
    this.source.uid = uid;
  }

  public set domain(domain: string) {
    this.source.domain = domain;
  }

  public get domain(): string {
    return this.source.domain;
  }

  /**  */
  public set businessUnitCode(businessUnitCode: string) {
    this.source.businessUnitCode = businessUnitCode;
  }
  public get businessUnitCode(): string {
    return this.source.businessUnitCode;
  }

  /**  */
  @field('string')
  public set code(code: string) {
    this.source.code = code;
  }
  public get code(): string {
    return this.source.code;
  }

  /**  */
  @field('string')
  public set freeDataCode(freeDataCode: string) {
    this.source.freeDataCode = freeDataCode;
  }
  public get freeDataCode(): string {
    return this.source.freeDataCode;
  }

  @field('TranslatedEmb')
  public set label(value: TranslatedEmb) {
    this.source.label = value;
  }
  public get label(): TranslatedEmb {
    return this.source.label;
  }

  @field('TranslatedEmb')
  public set valueControlMessage(value: TranslatedEmb) {
    this.source.valueControlMessage = value;
  }
  public get valueControlMessage(): TranslatedEmb {
    return this.source.valueControlMessage;
  }

  @field('TranslatedEmb')
  public set editRestrictMessage(value: TranslatedEmb) {
    this.source.editRestrictMessage = value;
  }
  public get editRestrictMessage(): TranslatedEmb {
    return this.source.editRestrictMessage;
  }


  /** type de champ */
  @field('string')
  public set dataType(value: string) {
    this.source.dataType = value;
  }
  public get dataType(): string {
    return this.source.dataType;
  }

  /** expression pour restriction */
  @field('string')
  public set restrict(value: string) {
    this.source.restrict = value;
  }
  public get restrict(): string {
    return this.source.restrict;
  }

  /** expression pour lock */
  @field('string')
  public set lock(value: string) {
    this.source.lock = value;
  }
  public get lock(): string {
    return this.source.lock;
  }


  /** fonction de référence pour contrôle */
  @field('string')
  public set validator(value: string) {
    this.source.validator = value;
  }
  public get validator(): string {
    return this.source.validator;
  }

  /** format de référence */
  @field('string')
  public set format(value: string) {
    this.source.format = value;
  }
  public get format(): string {
    return this.source.format;
  }

  /** FD de référence pour alimentation d'un mode 'drop' */
  @field('string')
  public set datas(value: string) {
    this.source.datas = value;
  }
  public get datas(): string {
    return this.source.datas;
  }

  /** valeur par défaut */
  @field('string')
  public set valor(value: string) {
    this.source.valor = value;
  }
  public get valor(): string {
    return this.source.valor;
  }

  /** déclencheur de valeur par défaut */
  @field('string')
  public set valorTrigger(value: string) {
    this.source.valorTrigger = value;
  }
  public get valorTrigger(): string {
    return this.source.valorTrigger;
  }


  /** valeur min */
  @field('number')
  public set minValue(value: number) {
    this.source.minValue = value;
  }
  public get minValue(): number {
    return this.source.minValue;
  }

  /** valeur max */
  @field('number')
  public set maxValue(value: number) {
    this.source.maxValue = value;
  }
  public get maxValue(): number {
    return this.source.maxValue;
  }

  /** action */
  @field('string')
  public set action(value: string) {
    this.source.action = value;
  }
  public get action(): string {
    return this.source.action;
  }

  @field('number')
  public set defaultSize(value: number) {
    this.source.defaultSize = value;
  }
  public get defaultSize(): number {
    return this.source.defaultSize;
  }

  @field('string')
  public set regex(value: string) {
    this.source.regex = value;
  }
  public get regex(): string {
    return this.source.regex;
  }

  @field('boolean')
  public set upper(value: boolean) {
    this.source.upper = value;
  }
  public get upper(): boolean {
    return this.source.upper;
  }

  /**
   * configuration complexe (mesures de type liste)
   */
  @field('string')
  public set settings(settings: any) {
      this.source.settings = settings;
  }
  public get settings(): any {
      return this.source.settings;
  }

  /**
   * gestion des settings en mode string pour simplification
   */
  private _settingsStr: string = null;
  public set settingsStr(value: string) {
      this._settingsStr = value;
  }

  public get settingsStr(): string {
      return this._settingsStr;
  }


  /* ################################################
                    Accesseurs composites
  ################################################ */

  public order: number = 100; // pour ordonner les champs génériques en premier
  public constantLabel: string = null; // label pour les champs de base

  /** Le free data est-il persisté? */
  public get exist(): boolean {
    // l'objet existe en BDD (id)
    return this.source.id > 0;
  }

  /** Libellé traductible */
  private _labelVO: TranslatableLabelVO;
  public get labelVO(): TranslatableLabelVO {
    if (!this._labelVO) {
      this._labelVO = Model.getInstance().localeEngine.newTranslatableVO(this, 'label');
    }
    return this._labelVO;
  }

  /** err sur restrict traductible */
  private _editRestrictMessageVO: TranslatableLabelVO;
  public get editRestrictMessageVO(): TranslatableLabelVO {
    if (!this._editRestrictMessageVO) {
      this._editRestrictMessageVO = Model.getInstance().localeEngine.newTranslatableVO(this, 'editRestrictMessage');
    }
    return this._editRestrictMessageVO;
  }

  /** err sur valueControl traductible */
  private _valueControlMessageVO: TranslatableLabelVO;
  public get valueControlMessageVO(): TranslatableLabelVO {
    if (!this._valueControlMessageVO) {
      this._valueControlMessageVO = Model.getInstance().localeEngine.newTranslatableVO(this, 'valueControlMessage');
    }
    return this._valueControlMessageVO;
  }

  /**
   * Return msgError
   * @return string
   */
   get trsValueControlMessage(): string {
    return Model.getInstance().localeEngine.translate(this.valueControlMessage, '$.common.error.$');
  }

  /**
   * Return msgError
   * @return string
   */
   get trsEditRestrictMessage(): string {
    return Model.getInstance().localeEngine.translate(this.editRestrictMessage, '$.common.error.$');
  }

  /**
   * Return translated label
   * @return string
   */
   get trsLabel(): string {
    if (this.constantLabel) {
      // console.log('exist constantLabel', this);
      return WizTranslationManager.getInstance().translateKey(this.constantLabel);
    } else {
      // console.log('missing constantLabel', this);
    }

    return Model.getInstance().localeEngine.translate(this.label, WizTranslationManager.getInstance().translateKey('$.arg.setlabel.$') );
  }
}
